import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { HeroForm } from "@/components/home/HeroForm";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Clock, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const contactInfo = [
  {
    icon: Phone,
    title: "Call Us",
    content: "(763) 245-7813",
    href: "tel:+17632457813",
  },
  {
    icon: Mail,
    title: "Email Us",
    content: "riekelslcl@gmail.com",
    href: "mailto:riekelslcl@gmail.com",
  },
  {
    icon: MapPin,
    title: "Our Service Area",
    content:
      "Maple Grove, Plymouth, Rogers, Medina, Corcoran, and surrounding West Metro areas",
  },
  {
    icon: Clock,
    title: "Available 7 Days a Week",
    content: "Mon to Fri - 7:00am to 7:00pm\nSat & Sun - By Appointment",
  },
];

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Contact Riekels Lawn Care & Landscaping for lawn care, landscaping, and snow removal services. Call (763) 245-7813. Serving the West Twin Cities Metro and surrounding areas."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Contact"
          title="Get in Touch"
          subtitle="We're here to help with all your lawn care and landscaping needs. Reach out today!"
        />

        {/* Schedule Appointment CTA */}
        <section className="py-12 bg-secondary" aria-labelledby="schedule-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" aria-hidden="true" />
              <h2 id="schedule-heading" className="text-2xl md:text-3xl font-heading font-bold text-secondary-foreground mb-3">
                Get Your Free Estimate
              </h2>
              <p className="text-secondary-foreground/80 mb-6">
                Fill out our quick form or give us a call. We'll get back to you promptly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-secondary font-semibold px-8 py-6 text-lg"
                >
                  <a href="tel:+17632457813">
                    <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                    Call Us Now
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  className="bg-white hover:bg-white/90 text-secondary font-semibold px-8 py-6 text-lg"
                >
                  <a href="https://lawnmammoth.lovable.app/embed/39673b4c-6825-43f1-9697-ad4e1647fba3" target="_blank" rel="noopener noreferrer">
                    Get Instant Estimate
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Request Service Form */}
        <section className="py-20" aria-labelledby="quote-form-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto flex flex-col items-center">
              <div className="text-center mb-10">
                <h2 id="quote-form-heading" className="text-3xl font-heading font-bold text-foreground mb-4">
                  Request a Free Quote
                </h2>
                <p className="text-lg text-muted-foreground">
                  Fill out the form below and we'll get back to you promptly.
                </p>
              </div>
              <HeroForm />
            </div>
          </div>
        </section>

        <section className="py-20 bg-section-alt" aria-labelledby="contact-methods-heading">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 id="contact-methods-heading" className="text-3xl font-heading font-bold text-foreground">
                Other Ways to Reach Us
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactInfo.map((item, index) => (
                <article key={index} className="contact-card overflow-hidden">
                  <div className="icon-circle flex-shrink-0" aria-hidden="true">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-bold text-foreground mb-1">
                      {item.title}
                    </h3>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-muted-foreground hover:text-secondary transition-colors break-all text-sm focus:outline-none focus-visible:text-secondary focus-visible:underline"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <p className="text-muted-foreground text-sm whitespace-pre-line">
                        {item.content}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;
